// ============================================================================
// Supply → Chemical stock. 2026-09-23. Three views of ONE log:
//   * Account manager — their stock location, their clinics sorted by who runs
//     out first. Any AM or location can be picked: anyone can log (decision 6).
//   * National        — every location, on hand, order-by dates, "order
//     together", clinics overdue, counts going stale, the changeover.
//   * Cost            — Jose's view: cost when used, per clinic, per chair, by
//     market, money on shelves. It lives here, once; Finance links to it.
//
// ⛔ Launch day is a real state: no purchase, no delivery, no count. Every
// number without its inputs says what is missing and who supplies it.
// ============================================================================

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../shared/auth';
import { ErrorState, PageHeader, Skeleton } from '../../../shared/components';
import { SupplySubNav } from '../supply-sub-nav';
import {
  fetchClinicStock,
  fetchCompanyRates,
  fetchDeliveryCosts,
  fetchDialysisRevenue,
  fetchLocationStock,
  fetchPhasingOutCounts,
  fetchProducts,
  fetchStockOffices,
  fetchStockPoLines,
  type ClinicStockRow,
  type LocationStockRow,
} from './api';
import {
  cleanerOf,
  fmtDay,
  fmtDays,
  fmtMoney,
  fmtQty,
  fmtRateDay,
  fmtRateMonth,
  isNum,
  locationMissing,
  needsDataLabel,
} from './format';
import { useStockLoad } from './use-stock-data';
import { LogDeliveryWindow } from './log-delivery-window';
import { UpdateCountWindow, type CountTarget } from './update-count-window';
import { TransferWindow } from './move-stock-windows';
import { StockSettingWindow, type SettingTarget } from './stock-setting-window';

type View = 'am' | 'national' | 'cost';

const BTN =
  'rounded border border-tcs-border bg-tcs-raised px-2.5 py-1 text-xs font-medium text-tcs-text hover:bg-tcs-surface';
const BTN_PRIMARY =
  'rounded border border-tcs-accent bg-tcs-accent-ink px-2.5 py-1 text-xs font-medium text-tcs-accent-on hover:opacity-90';

const QBO_LINE =
  'This is cost when used. QuickBooks shows the same money when it was bought; month to month they differ, over a quarter they meet.';

interface Clinic {
  facility_id: string;
  display: string;
  name: string;
  cleaner: string | null;
  am_eid: string | null;
  am_name: string | null;
  market: string | null;
  office: string | null;
  rows: ClinicStockRow[];
}

function groupClinics(rows: ClinicStockRow[]): Clinic[] {
  const m = new Map<string, Clinic>();
  for (const r of rows) {
    const id = r.facility_id ?? '';
    let c = m.get(id);
    if (!c) {
      c = {
        facility_id: id,
        display: r.display_name ?? r.facility_name ?? id,
        name: r.facility_name ?? id,
        cleaner: cleanerOf(r.display_name, r.facility_name),
        am_eid: r.account_manager_eid,
        am_name: r.account_manager_name,
        market: r.market_region,
        office: r.stock_office_id,
        rows: [],
      };
      m.set(id, c);
    }
    c.rows.push(r);
  }
  return Array.from(m.values());
}

const minDays = (c: Clinic) => {
  const ds = c.rows.map((r) => r.days_left).filter(isNum);
  return ds.length ? Math.min(...ds) : null;
};

export default function StockPage() {
  const [params, setParams] = useSearchParams();
  const view = (params.get('view') as View) || 'am';
  const setView = (v: View) => {
    const next = new URLSearchParams(params);
    next.set('view', v);
    setParams(next, { replace: true });
  };

  const clinicsQ = useStockLoad(() => fetchClinicStock(), [], [] as ClinicStockRow[]);
  const locationsQ = useStockLoad(() => fetchLocationStock(), [], [] as LocationStockRow[]);
  const refsQ = useStockLoad(
    async () => {
      const [offices, products, rates] = await Promise.all([fetchStockOffices(), fetchProducts(), fetchCompanyRates()]);
      return { offices, products, rates };
    },
    [],
    { offices: [], products: [], rates: [] } as {
      offices: Awaited<ReturnType<typeof fetchStockOffices>>;
      products: Awaited<ReturnType<typeof fetchProducts>>;
      rates: Awaited<ReturnType<typeof fetchCompanyRates>>;
    },
  );

  const clinics = useMemo(() => groupClinics(clinicsQ.data), [clinicsQ.data]);
  const officeName = useMemo(() => {
    const m: Record<string, string> = {};
    for (const o of refsQ.data.offices) m[o.office_id] = o.office_name ?? o.office_id;
    return m;
  }, [refsQ.data.offices]);

  const loading = clinicsQ.loading || locationsQ.loading || refsQ.loading;
  const error = clinicsQ.error || locationsQ.error || refsQ.error;

  return (
    <div className="mx-auto max-w-6xl p-4 pb-28 sm:p-6">
      <SupplySubNav />
      <PageHeader
        title="Dialysis chemical stock"
        meta={`Micro-Chem Plus (gallons) and Stearns Quart’r tabs (500 per case) · ${clinics.length} dialysis clinics · customers who supply their own chemicals are left out`}
      />
      <div className="mt-3 flex flex-wrap gap-1">
        {(
          [
            ['am', 'Account manager'],
            ['national', 'National'],
            ['cost', 'Cost'],
          ] as Array<[View, string]>
        ).map(([v, label]) => (
          <button
            key={v}
            type="button"
            onClick={() => setView(v)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${
              view === v ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'border border-tcs-border bg-tcs-surface text-tcs-text-secondary'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mt-4">
          <ErrorState body={error} onRetry={() => { clinicsQ.refetch(); locationsQ.refetch(); refsQ.refetch(); }} />
        </div>
      )}
      {loading && !error && (
        <div className="mt-4 space-y-3">
          <Skeleton width="100%" height={80} block />
          <Skeleton width="100%" height={240} block />
        </div>
      )}
      {!loading && !error && view === 'am' && (
        <AmView clinics={clinics} locations={locationsQ.data} officeName={officeName} refs={refsQ.data} />
      )}
      {!loading && !error && view === 'national' && (
        <NationalView clinics={clinics} locations={locationsQ.data} refs={refsQ.data} />
      )}
      {!loading && !error && view === 'cost' && <CostView clinics={clinics} locations={locationsQ.data} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Account manager
// ---------------------------------------------------------------------------

function AmView({
  clinics,
  locations,
  officeName,
  refs,
}: {
  clinics: Clinic[];
  locations: LocationStockRow[];
  officeName: Record<string, string>;
  refs: { offices: Awaited<ReturnType<typeof fetchStockOffices>>; products: Awaited<ReturnType<typeof fetchProducts>> };
}) {
  const { employee } = useAuth();
  const ams = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of clinics) if (c.am_eid) m.set(c.am_eid, c.am_name ?? c.am_eid);
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [clinics]);
  const mine = employee && ams.some(([id]) => id === employee.employee_id) ? employee.employee_id : '';
  const [am, setAm] = useState<string>(mine);
  const [deliveryFor, setDeliveryFor] = useState<Clinic | null>(null);
  const [countFor, setCountFor] = useState<CountTarget | null>(null);
  const [moveFrom, setMoveFrom] = useState<string | null>(null);

  const list = clinics
    .filter((c) => !am || c.am_eid === am)
    .sort((a, b) => {
      const da = minDays(a);
      const db = minDays(b);
      if (da == null && db == null) return a.name.localeCompare(b.name);
      if (da == null) return 1;
      if (db == null) return -1;
      return da - db;
    });
  const myOffices = Array.from(new Set(list.map((c) => c.office).filter((x): x is string => !!x)));
  const anyShelf = list.some((c) => c.rows.some((r) => r.has_shelf));

  return (
    <div className="mt-4 space-y-5">
      <label className="block max-w-sm">
        <span className="mb-1 block text-xs text-tcs-text-muted">Account manager</span>
        <select
          className="w-full rounded border border-tcs-border bg-tcs-field px-2 py-1.5 text-base sm:text-sm"
          value={am}
          onChange={(e) => setAm(e.target.value)}
        >
          <option value="">Every account manager</option>
          {ams.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </label>

      <section className="grid gap-3 md:grid-cols-2">
        {myOffices.map((oid) => (
          <LocationCard
            key={oid}
            officeId={oid}
            name={officeName[oid] ?? oid}
            rows={locations.filter((l) => l.office_id === oid)}
            onMove={() => setMoveFrom(oid)}
            onCount={() =>
              setCountFor({
                officeId: oid,
                title: officeName[oid] ?? oid,
                products: refs.products.map((p) => ({ supply_id: p.supply_id ?? '', unit_label: p.unit_label })),
              })
            }
          />
        ))}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-tcs-text">{am ? 'Your clinics' : 'Every clinic'}</h3>
        <p className="text-xs text-tcs-text-muted">
          {anyShelf
            ? 'Sorted by who runs out first.'
            : 'Nothing delivered yet. Starting estimates come from chairs and nights cleaned.'}
        </p>
        <div className="mt-2 overflow-x-auto rounded-lg border border-tcs-border">
          <table className="min-w-full text-sm">
            <thead className="bg-tcs-surface text-left text-xs text-tcs-text-muted">
              <tr>
                <th className="px-3 py-2">Clinic (cleaner)</th>
                <th className="px-3 py-2">Floors · Micro-Chem</th>
                <th className="px-3 py-2">Surfaces · Stearns</th>
                <th className="px-3 py-2">Last count</th>
                <th className="px-3 py-2">Next delivery</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-tcs-border">
              {list.map((c) => (
                <ClinicRow
                  key={c.facility_id}
                  c={c}
                  onLog={() => setDeliveryFor(c)}
                  onCount={() =>
                    setCountFor({
                      facilityId: c.facility_id,
                      title: c.display,
                      amEid: c.am_eid,
                      amName: c.am_name,
                      providerName: c.cleaner,
                      products: c.rows.map((r) => ({ supply_id: r.supply_id ?? '', unit_label: r.unit_label })),
                    })
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {deliveryFor && (
        <LogDeliveryWindow open onClose={() => setDeliveryFor(null)} rows={deliveryFor.rows} offices={refs.offices} />
      )}
      {countFor && <UpdateCountWindow open onClose={() => setCountFor(null)} target={countFor} />}
      <TransferWindow open={!!moveFrom} onClose={() => setMoveFrom(null)} offices={refs.offices} products={refs.products} defaultFrom={moveFrom} />
    </div>
  );
}

function ClinicRow({ c, onLog, onCount }: { c: Clinic; onLog: () => void; onCount: () => void }) {
  const mc = c.rows.find((r) => r.unit_label === 'gal');
  const st = c.rows.find((r) => r.unit_label === 'tabs');
  const first = c.rows[0];
  const nd = needsDataLabel(first);
  const cell = (r?: ClinicStockRow) => {
    if (!r) return '—';
    if (!r.has_shelf) {
      return (
        <span className="text-xs text-tcs-text-secondary">
          About {fmtRateMonth(r.rate_month, r.unit_label)}
          {r.is_estimated ? ' · estimated' : ''}
        </span>
      );
    }
    const low = isNum(r.days_left) && r.days_left <= Number(r.deliver_at_days ?? 7);
    return (
      <span className={low ? 'font-medium text-st-danger-fg' : 'text-tcs-text'}>
        {fmtDays(r.days_left)}
        <span className="ml-1 text-xs text-tcs-text-muted">({fmtQty(r.shelf_now, r.unit_label)})</span>
      </span>
    );
  };
  const lastCount = c.rows
    .filter((r) => r.last_count_at)
    .sort((a, b) => (a.last_count_at! < b.last_count_at! ? 1 : -1))[0];
  const days = minDays(c);
  const deliverAt = Number(first.deliver_at_days ?? 7);
  let next: string;
  if (days == null) {
    next = first.start_date && new Date(`${first.start_date}T12:00:00`).getTime() + 7 * 86_400_000 > Date.now()
      ? 'Awaiting first delivery · startup until start + 7 days'
      : 'Awaiting first delivery';
  } else if (days <= deliverAt) {
    next = 'Overdue';
  } else {
    next = fmtDay(new Date(Date.now() + (days - deliverAt) * 86_400_000).toISOString());
  }

  return (
    <tr>
      <td className="px-3 py-2">
        <Link
          to={`/account-management/facilities/${encodeURIComponent(c.facility_id)}?tab=supplies`}
          className="font-medium text-tcs-text hover:underline"
        >
          {c.cleaner ? c.display : `${c.name} (no cleaner on file)`}
        </Link>
        <div className="text-xs text-tcs-text-muted">
          {isNum(first.chair_count) && first.chair_count > 0
            ? `${first.chair_count} chair${first.chair_count === 1 ? '' : 's'}`
            : 'chairs not on file'}
          {isNum(first.nights_per_week) ? `, ${first.nights_per_week} nights` : ''}
        </div>
        {nd && <div className="text-[11px] text-st-warn-fg">{nd}</div>}
      </td>
      <td className="px-3 py-2">{cell(mc)}</td>
      <td className="px-3 py-2">{cell(st)}</td>
      <td className="px-3 py-2 text-xs text-tcs-text-secondary">
        {lastCount ? `${fmtDay(lastCount.last_count_at)} · ${lastCount.last_count_source === 'at_delivery' ? 'delivery' : lastCount.last_count_source === 'reported' ? 'night manager' : lastCount.last_count_source === 'inspection' ? 'inspection' : 'counted'}` : 'Not counted yet'}
      </td>
      <td className={`px-3 py-2 text-xs ${next === 'Overdue' ? 'font-medium text-st-danger-fg' : 'text-tcs-text-secondary'}`}>{next}</td>
      <td className="whitespace-nowrap px-3 py-2 text-right">
        <button type="button" className={BTN} onClick={onCount}>
          Count
        </button>{' '}
        <button type="button" className={BTN_PRIMARY} onClick={onLog}>
          Log
        </button>
      </td>
    </tr>
  );
}

function LocationCard({
  officeId,
  name,
  rows,
  onMove,
  onCount,
}: {
  officeId: string;
  name: string;
  rows: LocationStockRow[];
  onMove: () => void;
  onCount: () => void;
}) {
  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4" data-office={officeId}>
      <h3 className="text-sm font-semibold text-tcs-text">{name}</h3>
      <div className="mt-2 space-y-3">
        {rows.map((r) => {
          const missing = locationMissing(r);
          return (
            <div key={r.supply_id} className="rounded-lg border border-tcs-border bg-tcs-surface p-3">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-tcs-text">{r.unit_label === 'tabs' ? 'Stearns tabs' : 'Micro-Chem Plus, gallons'}</span>
                {r.order_by_date && <span className="text-xs font-medium text-st-warn-fg">Reorder by {fmtDay(r.order_by_date)}</span>}
              </div>
              {r.has_stock_record ? (
                <p className="mt-1 text-xs text-tcs-text-secondary">
                  {fmtQty(r.on_hand, r.unit_label)} on hand. Using {fmtRateDay(r.use_per_day, r.unit_label)} across{' '}
                  {r.clinics} clinic{r.clinics === 1 ? '' : 's'}
                  {isNum(r.coverage_days) ? `. Lasts ${fmtDays(r.coverage_days)}` : ''}.
                </p>
              ) : null}
              {missing && <p className="mt-1 text-xs text-tcs-text-muted">{missing}</p>}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        <button type="button" className={BTN} onClick={onMove}>
          Move stock between locations
        </button>
        <button type="button" className={BTN} onClick={onCount}>
          Correct the count here
        </button>
      </div>
      <p className="mt-3 text-[11px] text-tcs-text-muted">
        Storage from the SDS sheets: Micro-Chem between 50 °F and 120 °F. Stearns cool, dry, protected from humidity and
        freezing, locked up, out of reach of children.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// National
// ---------------------------------------------------------------------------

function NationalView({
  clinics,
  locations,
  refs,
}: {
  clinics: Clinic[];
  locations: LocationStockRow[];
  refs: {
    offices: Awaited<ReturnType<typeof fetchStockOffices>>;
    products: Awaited<ReturnType<typeof fetchProducts>>;
    rates: Awaited<ReturnType<typeof fetchCompanyRates>>;
  };
}) {
  const phasingQ = useStockLoad(() => fetchPhasingOutCounts(clinics.map((c) => c.facility_id)), [clinics.length], {} as Record<string, number>);
  const [setting, setSetting] = useState<SettingTarget | null>(null);
  // Every location that holds or receives stock, the dock included.
  const stockLocs = locations;
  const byOffice = new Map<string, LocationStockRow[]>();
  for (const l of stockLocs) byOffice.set(l.office_id ?? '', [...(byOffice.get(l.office_id ?? '') ?? []), l]);

  // "Order together": locations whose order-by dates fall within 6 days of each other.
  const due = Array.from(byOffice.entries())
    .map(([oid, rs]) => ({
      oid,
      name: rs[0]?.office_name ?? oid,
      date: rs.map((r) => r.order_by_date).filter((d): d is string => !!d).sort()[0] ?? null,
    }))
    .filter((d) => d.date)
    .sort((a, b) => (a.date! < b.date! ? -1 : 1));
  let together: string[] = [];
  for (let i = 0; i < due.length; i++) {
    const group = due.filter((d) => Math.abs(new Date(d.date!).getTime() - new Date(due[i].date!).getTime()) <= 6 * 86_400_000);
    if (group.length > together.length) together = group.map((g) => g.name);
  }

  const overdue = clinics.filter((c) => c.rows.some((r) => isNum(r.days_left) && r.days_left <= Number(r.deliver_at_days ?? 7)));
  const stale = clinics.filter((c) => c.rows.some((r) => isNum(r.count_age_days) && r.count_age_days > 45));
  const total = clinics.length;
  const listSet = clinics.length;
  const firstDelivered = clinics.filter((c) => c.rows.some((r) => (r.deliveries ?? 0) > 0)).length;
  const sdsAndOld = clinics.filter((c) => c.rows[0]?.sds_posted && (phasingQ.data[c.facility_id] ?? 0) === 0).length;
  const dataComplete = clinics.filter((c) => !c.rows[0]?.needs_data).length;

  return (
    <div className="mt-4 space-y-6">
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-tcs-text">Stock by location</h3>
          <Link to="/supply/orders/new-stock" className={BTN_PRIMARY}>
            Record a purchase
          </Link>
        </div>
        <p className="text-xs text-tcs-text-muted">
          Lead time is learned from each purchase: invoice date to the day it arrived.
        </p>
        {/* No "On order" column (TCSD-0387): a stock purchase is entered once it has
            arrived and is received in the same step, so on order is always zero. The
            on-order math stays in v_stock_po_lines / v_stock_location_stock, untouched,
            for the day ordering moves into the app. */}
        <div className="mt-2 overflow-x-auto rounded-lg border border-tcs-border">
          <table className="min-w-full text-sm">
            <thead className="bg-tcs-surface text-left text-xs text-tcs-text-muted">
              <tr>
                <th className="px-3 py-2">Location</th>
                <th className="px-3 py-2">Product</th>
                <th className="px-3 py-2">On hand</th>
                <th className="px-3 py-2">Use / day</th>
                <th className="px-3 py-2">Days left</th>
                <th className="px-3 py-2">Order by</th>
                <th className="px-3 py-2">Suggested</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tcs-border">
              {stockLocs.map((l) => {
                const missing = locationMissing(l);
                return (
                  <tr key={`${l.office_id}-${l.supply_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-tcs-text">{l.office_name}</div>
                      <div className="text-xs text-tcs-text-muted">
                        {l.stock_role === 'receive_only' ? 'Receives freight only' : `${l.clinics} clinics`}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs">{l.unit_label === 'tabs' ? 'Stearns' : 'Micro-Chem'}</td>
                    <td className="px-3 py-2">{l.has_stock_record ? fmtQty(l.on_hand, l.unit_label) : <span className="text-xs text-tcs-text-muted">No purchase yet</span>}</td>
                    <td className="px-3 py-2 text-xs">{fmtRateDay(l.use_per_day, l.unit_label)}</td>
                    <td className="px-3 py-2">{isNum(l.coverage_days) ? fmtDays(l.coverage_days) : '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {l.order_by_date ? (new Date(l.order_by_date) <= new Date() ? 'Now' : fmtDay(l.order_by_date)) : missing ? <span className="text-tcs-text-muted">{missing.startsWith('Needs a lead') ? 'Needs lead time' : missing.startsWith('No stock') ? 'Needs a purchase' : '—'}</span> : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">{isNum(l.suggested_cases) && l.suggested_cases > 0 ? `${l.suggested_cases} case${l.suggested_cases === 1 ? '' : 's'}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {together.length > 1 && (
          <p className="mt-2 rounded border border-st-info-border bg-st-info-bg px-3 py-2 text-xs text-st-info-fg">
            Order together: {together.join(', ')} all fall due within 6 days. One order to all of them saves freight.
          </p>
        )}
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
          <h3 className="text-sm font-semibold text-tcs-text">Changeover progress</h3>
          <ul className="mt-2 space-y-1 text-sm">
            <Progress label="Chemical list set (dialysis only)" n={listSet} of={total} />
            <Progress label="First delivery made" n={firstDelivered} of={total} />
            <Progress label="SDS posted and old stock picked up" n={sdsAndOld} of={total} />
            <Progress label="Clinic data complete (nights, chairs)" n={dataComplete} of={total} />
          </ul>
        </div>
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
          <h3 className="text-sm font-semibold text-tcs-text">Clinics overdue for a delivery</h3>
          {overdue.length === 0 ? (
            <p className="mt-1 text-xs text-tcs-text-secondary">
              {clinics.some((c) => c.rows.some((r) => r.has_shelf))
                ? 'None right now.'
                : 'Nothing to flag yet. A clinic shows here once it has a shelf level and it falls to its deliver-at point.'}
            </p>
          ) : (
            <ul className="mt-1 space-y-1 text-xs">
              {overdue.map((c) => (
                <li key={c.facility_id}>
                  <Link to={`/account-management/facilities/${encodeURIComponent(c.facility_id)}?tab=supplies`} className="text-tcs-text hover:underline">
                    {c.cleaner ? c.display : `${c.name} (no cleaner on file)`}
                  </Link>{' '}
                  <span className="text-tcs-text-muted">· {c.am_name ?? 'no AM'} · {fmtDays(minDays(c))} left</span>
                </li>
              ))}
            </ul>
          )}
          <h3 className="mt-4 text-sm font-semibold text-tcs-text">Counts going stale</h3>
          {stale.length === 0 ? (
            <p className="mt-1 text-xs text-tcs-text-secondary">Nothing to flag yet. A clinic shows here 45 days after its last count.</p>
          ) : (
            <ul className="mt-1 space-y-1 text-xs">
              {stale
                .sort((a, b) => (a.am_name ?? '').localeCompare(b.am_name ?? ''))
                .map((c) => (
                  <li key={c.facility_id}>
                    <span className="text-tcs-text-muted">{c.am_name ?? 'No AM'}:</span> {c.display}{' '}
                    <span className="text-tcs-text-muted">
                      · {fmtDays(Math.max(...c.rows.map((r) => Number(r.count_age_days ?? 0))))}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
        <h3 className="text-sm font-semibold text-tcs-text">Company starting rate</h3>
        <p className="text-xs text-tcs-text-muted">
          Used for every clinic that has not been counted: rate × chairs × nights cleaned. Replaced by what clinics really
          use once 10 clinics have a real observation.
        </p>
        <ul className="mt-2 space-y-1 text-sm">
          {refs.rates.map((r) => {
            const unit = r.supply_id === 'SUP-0230' ? 'tabs' : 'gal';
            return (
              <li key={r.supply_id} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-tcs-text">
                  {unit === 'tabs' ? 'Stearns' : 'Micro-Chem'}: {r.rate_per_chair_night} {unit} per chair per night
                  <span className="ml-2 text-xs text-tcs-text-muted">
                    {r.rate_source === 'learned'
                      ? `learned from ${r.clinics_observed} clinics`
                      : `starting placeholder · ${r.clinics_observed} of 10 clinics observed`}
                  </span>
                </span>
                <button
                  type="button"
                  className={BTN}
                  onClick={() =>
                    setSetting({
                      kind: 'company_rate',
                      supplyId: r.supply_id ?? '',
                      title: unit === 'tabs' ? 'Stearns tabs' : 'Micro-Chem Plus',
                      unitLabel: unit,
                      current: r.starting_rate,
                    })
                  }
                >
                  Edit
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <p className="text-xs text-tcs-text-muted">
        Storage from the SDS sheets: Micro-Chem between 50 °F and 120 °F. Stearns cool, dry, protected from humidity and
        freezing, locked up, out of reach of children.
      </p>
      <StockSettingWindow open={!!setting} onClose={() => setSetting(null)} target={setting} />
    </div>
  );
}

function Progress({ label, n, of }: { label: string; n: number; of: number }) {
  return (
    <li className="flex justify-between gap-2">
      <span className="text-tcs-text-secondary">{label}</span>
      <span className="font-medium text-tcs-text">
        {n} of {of}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Cost — Jose
// ---------------------------------------------------------------------------

function CostView({ clinics, locations }: { clinics: Clinic[]; locations: LocationStockRow[] }) {
  const costsQ = useStockLoad(() => fetchDeliveryCosts(), [], [] as Awaited<ReturnType<typeof fetchDeliveryCosts>>);
  const poQ = useStockLoad(() => fetchStockPoLines(), [], [] as Awaited<ReturnType<typeof fetchStockPoLines>>);
  const revQ = useStockLoad(() => fetchDialysisRevenue(clinics.map((c) => c.facility_id)), [clinics.length], null as number | null);

  const costs = costsQ.data;
  const operating = costs.filter((c) => !c.is_startup && c.cost != null);
  const startup = costs.filter((c) => c.is_startup && c.cost != null);
  const since = Date.now() - 30 * 86_400_000;
  const last30 = operating.filter((c) => new Date(c.occurred_at ?? 0).getTime() >= since).reduce((s, c) => s + Number(c.cost), 0);
  const thisMonth = new Date().toISOString().slice(0, 7);

  const byMonth = new Map<string, number>();
  for (const c of operating) byMonth.set(c.month ?? '', (byMonth.get(c.month ?? '') ?? 0) + Number(c.cost));
  const months = Array.from(byMonth.entries()).sort(([a], [b]) => (a < b ? -1 : 1));
  const fullMonths = months.filter(([m]) => m.slice(0, 7) !== thisMonth);

  // Per clinic per month = the last full month's operating cost ÷ clinics that had a delivery.
  const lastFull = fullMonths[fullMonths.length - 1];
  const lastFullClinics = lastFull ? new Set(operating.filter((c) => c.month === lastFull[0]).map((c) => c.facility_id)) : new Set();
  const perClinic = lastFull && lastFullClinics.size > 0 ? lastFull[1] / lastFullClinics.size : null;

  const clinicsWithTwoCounts = clinics.filter((c) => c.rows.some((r) => (r.n_obs ?? 0) >= 1)).length;
  const averagesReady = clinicsWithTwoCounts >= 10;
  const noChairs = clinics.filter((c) => !(isNum(c.rows[0]?.chair_count) && c.rows[0].chair_count > 0)).length;

  // Per chair per month over the last full month, clinics with real chairs only.
  const perChairByClinic = new Map<string, number>();
  if (lastFull) {
    for (const c of operating.filter((x) => x.month === lastFull[0] && isNum(x.chair_count) && x.chair_count > 0)) {
      perChairByClinic.set(c.facility_id ?? '', (perChairByClinic.get(c.facility_id ?? '') ?? 0) + Number(c.cost) / Number(c.chair_count));
    }
  }
  const perChairVals = Array.from(perChairByClinic.values());
  const perChair = perChairVals.length ? perChairVals.reduce((s, v) => s + v, 0) / perChairVals.length : null;
  const outliers = perChair
    ? Array.from(perChairByClinic.entries()).filter(([, v]) => v > 1.75 * perChair)
    : [];

  const share = revQ.data && revQ.data > 0 && operating.length > 0 ? last30 / revQ.data : null;

  const markets = new Map<string, { clinics: Set<string>; cost: number; startup: Set<string> }>();
  for (const c of clinics) {
    const m = c.market ?? 'No market';
    if (!markets.has(m)) markets.set(m, { clinics: new Set(), cost: 0, startup: new Set() });
    markets.get(m)!.clinics.add(c.facility_id);
  }
  for (const c of operating.filter((x) => new Date(x.occurred_at ?? 0).getTime() >= since)) {
    const m = markets.get(c.market_region ?? 'No market');
    if (m) m.cost += Number(c.cost);
  }
  for (const c of startup) markets.get(c.market_region ?? 'No market')?.startup.add(c.facility_id ?? '');

  const shelves = new Map<string, number>();
  for (const l of locations) {
    if (!isNum(l.value_on_shelf)) continue;
    shelves.set(l.office_name ?? '', (shelves.get(l.office_name ?? '') ?? 0) + l.value_on_shelf);
  }
  const received = poQ.data.filter((p) => p.received_units != null);

  return (
    <div className="mt-4 space-y-5">
      <p className="rounded border border-st-info-border bg-st-info-bg px-3 py-2 text-xs text-st-info-fg">{QBO_LINE}</p>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Chemical cost, last 30 days"
          value={operating.length ? fmtMoney(last30, 0) : null}
          missing="None yet — starts with the first delivery"
        />
        <Tile
          label="Per clinic per month"
          value={perClinic != null ? fmtMoney(perClinic) : null}
          missing="Needs a full month of deliveries"
        />
        <Tile
          label="Per chair per month"
          value={averagesReady && perChair != null ? fmtMoney(perChair) : null}
          missing={`Needs 10 clinics with two counts (${clinicsWithTwoCounts} so far)`}
          note={averagesReady && noChairs > 0 ? `${noChairs} clinics without chair counts excluded` : undefined}
        />
        <Tile
          label="Share of dialysis revenue"
          value={share != null ? `${(share * 100).toFixed(1)}%` : null}
          missing={revQ.error ? 'Revenue could not be read' : 'Needs cost and revenue'}
        />
      </section>

      <section>
        <h3 className="text-sm font-semibold text-tcs-text">By month</h3>
        {months.length === 0 && (
          <p className="text-xs text-tcs-text-secondary">
            Nothing to report yet. The first full month appears the month after first deliveries. Until then this page
            shows purchases received and stock on shelves.
          </p>
        )}
        {months.length === 1 && (
          <p className="text-sm text-tcs-text">
            {new Date(`${months[0][0]}T12:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            {months[0][0].slice(0, 7) === thisMonth ? ' (partial)' : ''}: <strong>{fmtMoney(months[0][1])}</strong>
          </p>
        )}
        {months.length >= 2 && (
          <ul className="mt-1 space-y-1">
            {months.map(([m, v]) => {
              const max = Math.max(...months.map(([, x]) => x));
              return (
                <li key={m} className="flex items-center gap-2 text-xs">
                  <span className="w-28 text-tcs-text-secondary">
                    {new Date(`${m}T12:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    {m.slice(0, 7) === thisMonth ? ' (partial)' : ''}
                  </span>
                  <span className="h-2 rounded bg-st-info-solid" style={{ width: `${Math.max(2, (v / max) * 60)}%` }} />
                  <span className="text-tcs-text">{fmtMoney(v, 0)}</span>
                </li>
              );
            })}
          </ul>
        )}
        {startup.length > 0 && (
          <p className="mt-1 text-xs text-tcs-text-muted">
            Startup deliveries ({new Set(startup.map((s) => s.facility_id)).size} clinics,{' '}
            {fmtMoney(startup.reduce((s, c) => s + Number(c.cost), 0))}) are reported with startup cost, not here.
          </p>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-tcs-text">By market, last 30 days</h3>
        <div className="mt-2 overflow-x-auto rounded-lg border border-tcs-border">
          <table className="min-w-full text-sm">
            <thead className="bg-tcs-surface text-left text-xs text-tcs-text-muted">
              <tr>
                <th className="px-3 py-2">Market</th>
                <th className="px-3 py-2">Clinics</th>
                <th className="px-3 py-2">Cost</th>
                <th className="px-3 py-2">Per clinic</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-tcs-border">
              {Array.from(markets.entries())
                .sort((a, b) => b[1].clinics.size - a[1].clinics.size)
                .map(([m, v]) => (
                  <tr key={m}>
                    <td className="px-3 py-2">{m.replace(/ Metro$/, '')}</td>
                    <td className="px-3 py-2">
                      {v.clinics.size - v.startup.size}
                      {v.startup.size > 0 ? ` +${v.startup.size} startup` : ''}
                    </td>
                    <td className="px-3 py-2">{operating.length ? fmtMoney(v.cost, 0) : '—'}</td>
                    <td className="px-3 py-2">{operating.length && v.clinics.size ? fmtMoney(v.cost / v.clinics.size) : '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
          <h3 className="text-sm font-semibold text-tcs-text">Money on shelves</h3>
          {shelves.size === 0 ? (
            <p className="mt-1 text-xs text-tcs-text-secondary">$0 — updates when a purchase is received.</p>
          ) : (
            <ul className="mt-1 space-y-1 text-sm">
              {Array.from(shelves.entries()).map(([n, v]) => (
                <li key={n} className="flex justify-between">
                  <span className="text-tcs-text-secondary">{n}</span>
                  <span className="font-medium text-tcs-text">{fmtMoney(v, 0)}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-tcs-text-muted">
            Purchases received so far: {received.length === 0 ? 'none' : `${received.length} line${received.length === 1 ? '' : 's'}`}.
          </p>
        </div>
        <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
          <h3 className="text-sm font-semibold text-tcs-text">Using far more than peers</h3>
          {!averagesReady ? (
            <p className="mt-1 text-xs text-tcs-text-secondary">
              Hidden until 10 clinics have two counts ({clinicsWithTwoCounts} so far) — with fewer, one clinic’s typo would
              mislead everyone.
            </p>
          ) : outliers.length === 0 ? (
            <p className="mt-1 text-xs text-tcs-text-secondary">No clinic is over 1.75 times the per-chair average.</p>
          ) : (
            <ul className="mt-1 space-y-1 text-xs">
              {outliers.map(([fid, v]) => {
                const c = clinics.find((x) => x.facility_id === fid);
                return (
                  <li key={fid}>
                    {c?.display ?? fid} — {fmtMoney(v)} per chair
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function Tile({ label, value, missing, note }: { label: string; value: string | null; missing: string; note?: string }) {
  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg p-4">
      <div className="text-xs text-tcs-text-muted">{label}</div>
      {value ? (
        <div className="mt-1 text-xl font-semibold text-tcs-text">{value}</div>
      ) : (
        <div className="mt-1 text-sm text-tcs-text-secondary">{missing}</div>
      )}
      {note && <div className="mt-1 text-[11px] text-tcs-text-muted">{note}</div>}
    </div>
  );
}
